'use client';
import * as m from 'motion/react-m';
import styled, { css } from 'styled-components';
import { easeInOutCubic, timingDefault, timingFaster } from '../../constants';
import { focusOutline, focusOutlineWithin } from '../../utility-classes';
import { StyledCheckbox } from '../StyledCheckbox';
import { TypeStyle } from '../TypeStyle';
const rowHoverBorderColor = ({ theme }) => theme.color.stroke.neutral.border.interaction.secondary.on.background;
export const Root = styled.div `
  display: flex;
  flex-direction: column;
  position: relative;
  gap: ${o => o.theme.spacing(5)};
  color: ${o => o.theme.color.text.primary.on.background};
  width: 100%;
`;
export const ScrollViewport = styled.div `
  position: relative;
  overflow: hidden;
  /* The top border for the header row */
  border-top: 1px solid ${o => o.theme.color.stroke.neutral.border.decoration.on.strong};
  border-radius: ${o => o.theme.borderRadius.md};

  /* The table's last row closes the card's bottom corners itself, but only when there's
     no Footer following it in here (i.e. when a Footer is present), it's the last (and
     bottom-most) child instead and owns the closing corners itself */
  &:not(:has(> [data-datatable-footer])) tbody > tr:last-child > td:first-child {
    border-bottom-left-radius: ${o => o.theme.borderRadius.md};
  }

  &:not(:has(> [data-datatable-footer])) tbody > tr:last-child > td:last-child {
    border-bottom-right-radius: ${o => o.theme.borderRadius.md};
  }
`;
export const ScrollContainer = styled.div `
  overflow: auto;
  position: relative;
`;
export const ScrollAreaRoot = styled.div `
  position: relative;
`;
export const LoadingOverlay = styled.div `
  position: absolute;
  inset: 0;
  z-index: 2;
  background: ${o => o.theme.color.fill.background};
  opacity: 0.5;
`;
export const StyledTable = styled.table `
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
  text-align: start;
  table-layout: fixed;

  /* Recolor the header border when the first row is hovered so the outline stays
     continuous. Hover-gated so touch doesn't latch it on tap/swipe. */
  @media (hover: hover) {
    & > thead:has(+ tbody > tr[data-datatable-row]:first-child:hover) > tr > th {
      border-bottom-color: ${rowHoverBorderColor};
    }
  }
`;
export const StyledHeaderCell = styled.th `
  position: sticky;
  top: 0;
  /* Keep fixed-width columns at their configured size instead of size + padding. */
  box-sizing: border-box;
  padding: ${o => o.theme.spacing(3, 2)};
  text-align: start;
  white-space: nowrap;
  vertical-align: middle;
  border-bottom: 1px solid ${o => o.theme.color.stroke.neutral.border.decoration.on.strong};
  background: ${o => o.theme.color.fill.background};
  color: ${o => o.theme.color.text.primary.on.background};
  z-index: 1;
  ${TypeStyle.css.bodyMicro};
  font-weight: 600;
  transition: border-color ${timingDefault} ease;

  /* Wider gutter against the table's outer edges than between interior columns. Also
     owns the table's left/right border within the header so that it lines up with the same
     edge the body rows enforce */
  &:first-child {
    padding-inline-start: ${o => o.theme.spacing(3)};
    border-left: 1px solid ${o => o.theme.color.stroke.neutral.border.decoration.on.strong};
    border-top-left-radius: ${o => o.theme.borderRadius.md};
  }

  &:last-child {
    padding-inline-end: ${o => o.theme.spacing(3)};
    border-right: 1px solid ${o => o.theme.color.stroke.neutral.border.decoration.on.strong};
    border-top-right-radius: ${o => o.theme.borderRadius.md};
  }
`;
export const SortButton = styled.button `
  display: inline-flex;
  align-items: center;
  gap: ${o => o.theme.spacing(1)};
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
  text-align: inherit;
  border-radius: ${o => o.theme.borderRadius.sm};
  ${focusOutline};
`;
export const SortIndicator = styled.span `
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 0;

  /* The Triangle icon has whitespace baked into its viewBox; pull the arrows together. */
  > *:first-child {
    margin-bottom: -2px;
  }
`;
export const SortArrow = styled.span `
  display: inline-flex;
  color: ${o => o.$active
    ? o.theme.color.button.primary.fill.on.background
    : o.theme.color.button.disabled.fill.on.background};
  transition: color ${timingDefault} ease;
`;
export const Body = styled.tbody `
  & > tr > td {
    transition: border-color ${timingDefault} ease;
  }

  & > tr + tr > td {
    border-top: 1px solid ${o => o.theme.color.stroke.neutral.border.decoration.on.strong};
  }

  & > tr:last-child > td {
    border-bottom: 1px solid ${o => o.theme.color.stroke.neutral.border.decoration.on.strong};
  }

  /* Hover outline (content rows only, not the empty/skeleton state rows): recolor the
     hovered row's own borders in place, rather than overlaying a separate shadow, so
     there's never a second line beside the one the cell already owns. */
  @media (hover: hover) {
    & > tr[data-datatable-row]:hover > td,
    & > tr[data-datatable-row]:hover + tr > td {
      border-top-color: ${rowHoverBorderColor};
    }

    & > tr[data-datatable-row]:last-child:hover > td {
      border-bottom-color: ${rowHoverBorderColor};
    }

    & > tr[data-datatable-row]:hover > td:first-child {
      border-left-color: ${rowHoverBorderColor};
    }

    & > tr[data-datatable-row]:hover > td:last-child {
      border-right-color: ${rowHoverBorderColor};
    }
  }
`;
export const StyledRow = styled.tr `
  background: transparent;
  transition: background-color ${timingDefault} ease;

  ${o => o.$selected &&
    css `
      background: ${o.theme.color.fill.subtle};
    `}
`;
export const StyledCell = styled.td `
  box-sizing: border-box;
  padding: ${o => o.theme.spacing(3, 2)};
  vertical-align: middle;
  ${TypeStyle.css.bodyMicro};
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  /* Wider gutter against the table's outer edges than between interior columns. Also
     owns the table's left/right frame edge (see StyledHeaderCell for the header's side). */
  &:first-child {
    padding-inline-start: ${o => o.theme.spacing(3)};
    border-left: 1px solid ${o => o.theme.color.stroke.neutral.border.decoration.on.strong};
  }

  &:last-child {
    padding-inline-end: ${o => o.theme.spacing(3)};
    border-right: 1px solid ${o => o.theme.color.stroke.neutral.border.decoration.on.strong};
  }
`;
export const SelectionCheckboxSlot = styled.div `
  display: flex;
  align-items: center;
  /* Left-anchored so the checkbox holds its position under table-layout: fixed. */
  justify-content: flex-start;
  width: 100%;
`;
export const SelectionCheckboxElement = styled(StyledCheckbox.Checkbox) `
  margin: 0;
  margin-inline-end: 0;
  padding: 0;
  /* Reset the base checkbox's 2px nudge so the box centers on the row. */
  top: 0;

  /* Hidden (not display:none) so it stays in flow as a spacer on non-selectable rows. */
  ${o => o.$hidden &&
    css `
      visibility: hidden;
    `}
`;
export const ToolbarWrapper = styled.div `
  container-name: toolbar-wrapper;
  container-type: inline-size;
`;
export const ToolbarInner = styled.div `
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${o => o.theme.spacing(5)};
  flex-wrap: wrap;
  @container toolbar-wrapper (min-width: 715px) {
    gap: ${o => o.theme.spacing(10)};
  }
`;
export const ToolbarActions = styled.div `
  display: flex;
  align-items: center;
  gap: ${o => o.theme.spacing(2)};
  flex-direction: column;
  width: 100%;
  align-items: stretch;
  @container toolbar-wrapper (min-width: 715px) {
    flex-direction: row;
    width: auto;
    align-items: center;
  }
`;
export const SearchFieldContainer = styled.div `
  display: flex;
  align-items: center;
  gap: ${o => o.theme.spacing(1)};
  width: 100%;
  flex-grow: 1;
  @container toolbar-wrapper (min-width: 715px) {
    width: auto;
  }
`;
export const SearchFieldAdornment = styled.div ``;
export const FilterButtonElement = styled.button `
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${o => o.theme.spacing(1)};
  transition: ${timingFaster} ${easeInOutCubic} color;
  color: ${o => o.theme.color.stroke.neutral.border.interaction.primary.on.background};
  &:hover,
  &:focus-visible {
    color: ${o => o.theme.color.stroke.neutral.border.interaction.secondary.on.background};
  }
  &:disabled,
  &[aria-disabled='true'] {
    color: ${o => o.theme.color.stroke.neutral.border.interaction.tertiary.on.background};
    cursor: not-allowed;
  }
`;
export const SearchField = styled.div `
  position: relative;
  width: 100%;
  min-width: 0;
  border-radius: ${o => o.theme.borderRadius.full};
  ${focusOutlineWithin};

  @container toolbar-wrapper (min-width: 715px) {
    min-width: 250px;
    max-width: 390px;
  }

  &:before {
    border-radius: inherit;
  }
`;
export const SearchInput = styled.input `
  appearance: none;
  box-sizing: border-box;
  width: 100%;
  padding: ${o => o.theme.spacing(2)};
  /* Reserve room on the leading edge for the absolutely-positioned search icon. */
  padding-inline-start: ${o => o.theme.spacing(10)};
  border: 1px solid ${o => o.theme.color.stroke.neutral.border.decoration.on.strong};
  border-radius: ${o => o.theme.borderRadius.full};
  outline: none;
  background: ${o => o.theme.color.fill.background};
  color: ${o => o.theme.color.text.primary.on.background};
  ${TypeStyle.css.bodyMicro};
  transition:
    background-color ${timingDefault} ease,
    border-color ${timingDefault} ease,
    opacity ${timingDefault} ease;

  &::placeholder {
    color: ${o => o.theme.color.text.secondary.on.background};
    font-weight: 400;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    background: ${o => o.theme.color.fill.subtle};
  }
`;
export const SearchIconWrapper = styled.div `
  position: absolute;
  inset-inline-start: ${o => o.theme.spacing(2)};
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  pointer-events: none;
  color: ${o => o.theme.color.text.secondary.on.background};
`;
export const StateMessage = styled.div `
  padding: ${o => o.theme.spacing(6)};
  text-align: center;
  color: ${o => o.theme.color.text.secondary.on.background};
`;
export const Footer = styled.div `
  /* When rendered, this is the last child inside ScrollViewport (see the DataTable.Footer
     docstring), so it picks up the closing left/right/bottom frame that the table's own
     last row only reaches down to when there's no footer following it. No border-top: the
     table's last row already owns that divider (see Body), and adding one here would
     double up against it. */
  padding: ${o => o.theme.spacing(3, 4)};
  border-left: 1px solid ${o => o.theme.color.stroke.neutral.border.decoration.on.strong};
  border-right: 1px solid ${o => o.theme.color.stroke.neutral.border.decoration.on.strong};
  border-bottom: 1px solid ${o => o.theme.color.stroke.neutral.border.decoration.on.strong};
  border-bottom-left-radius: ${o => o.theme.borderRadius.md};
  border-bottom-right-radius: ${o => o.theme.borderRadius.md};
`;
export const PaginationNav = styled.nav `
  display: flex;
  align-items: center;
  gap: ${o => o.theme.spacing(2)};
  width: 100%;
`;
export const PaginationLabel = styled.span `
  color: ${o => o.theme.color.text.secondary.on.background};
  white-space: nowrap;
  ${TypeStyle.css.bodyMicro};
`;
export const PaginationControls = styled.div `
  display: flex;
  align-items: center;
  gap: ${o => o.theme.spacing(2)};
`;
export const PaginationInfo = styled.div `
  display: flex;
  align-items: center;
  gap: ${o => o.theme.spacing(3)};
  margin-inline-start: auto;
`;
export const PaginationButton = styled.button `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: ${o => o.theme.spacing(1)};
  box-sizing: border-box;
  background: ${o => o.theme.color.fill.subtle};
  color: ${o => o.theme.color.text.primary.on.background};
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition:
    background-color ${timingDefault} ease,
    opacity ${timingDefault} ease;
  ${focusOutline};

  &:hover:not(:disabled) {
    background: ${o => o.theme.color.fill.disabled.subtle};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
    color: ${o => o.theme.color.text.secondary.on.background};
  }
`;
export const FiltersBar = styled.div `
  display: flex;
  flex-wrap: wrap;
  gap: ${o => o.theme.spacing(2)};
`;
export const FloatingTableActionsContainer = styled(m.div) `
  position: sticky;
  bottom: 0;
  display: flex;
  justify-content: center;
  bottom: ${o => o.theme.spacing(4)};
  pointer-events: none;
`;
export const FloatingTableActionsCard = styled.div `
  background: ${o => o.theme.color.fill.background};
  box-shadow: 0 8px 16px 0 rgba(0, 0, 0, 0.2);
  border: 1px solid ${o => o.theme.color.stroke.neutral.border.decoration.on.background};
  padding: ${o => o.theme.spacing(2, 3)};
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: ${o => o.theme.spacing(8)};
  pointer-events: auto;
`;
export const FloatingTableActionsCardContent = styled.div `
  display: flex;
  align-items: center;
`;
export const FloatingTableActionsMenuContainer = styled.div `
  display: flex;
  align-items: center;
`;
export const FloatingTableActionsMenuButtonElement = styled.button `
  border: 1px solid ${o => o.theme.color.stroke.neutral.border.interaction.primary.on.background};
  border-radius: 4px;
  padding: ${o => o.theme.spacing(1.5, 2.5)};
  display: flex;
  gap: ${o => o.theme.spacing(2)};
  align-items: center;
  transition: 0.125s ease-out border-color;

  &:hover,
  &[aria-expanded='true'] {
    border-color: ${o => o.theme.color.stroke.neutral.border.interaction.tertiary.on.background};
  }
`;
export const FloatingTableActionsMenuButtonText = styled(TypeStyle.BodyMicro).attrs(props => ({
    fontWeight: props.fontWeight ?? 600,
})) ``;
export const FloatingTableActionsMenuButtonIcon = styled.span ``;
//# sourceMappingURL=styles.js.map